import * as React from 'react';
import { Stack, SearchBox } from '@fluentui/react';
import { ActionContext } from '../../context/ActionContext';
import { CmicContext } from '../../context/CmicContext';
import { CMIC_TREE_NODE_TYPES, SPLIT_SIZES } from '../../shared/Constants';
import { customFields } from './services/services';
import { handleTextTheme, searchInputStyles } from '../../shared/styles';
import { parentStackStyles, HeaderMargin } from '../../utility/tasks/styles';
import {
	gatherAttchData,
	handleAttachmentVisiblity,
	handleDisableColumnMenu,
	uploadAttachments,
	uploadRevisions,
} from './services/utility';

import ActionUploadAttachment from '../tasks/upload/ActionUploadAttachment';
import CustomDatagrid from './components/CustomDatagrid';
import definedGridColumns from '../../utility/grid/GridColumns';
import definedGridRows from '../../utility/grid/GridRows';
import UploadDocumentAttch from './UploadDocumentAttch';
import useDebounce from '../../hooks/useDebounce';
import HeaderAttchDocuments from './components/HeaderAttchDocuments';
import { isInternetAvailable } from '../../shared/UtilityFunctions';

const AttDocumesnts = () => {
	const { useCallback, useContext, useEffect, useState } = React;

	// Defining
	const { userInfo, currentOfficeTheme, selectedProject, setShowToast } = useContext(CmicContext);
	const {
		nodeData = { items: [], totalResults: 0, hasMore: false, statusData: [] },
		activeNode,
		breadcrumbs,
		docDataForUpload,
		pageInfo,
		isLoading,
		itemFromEmail,
		setItemFromEmail,
		handleParentCountUpdate,
		fetchDataPagination,
	} = useContext(ActionContext);

	const [rows, setRows] = useState([]);
	const [columns, setColumns] = useState([]);
	const [requiredFields, setRequiredFields] = useState([]);
	const [selectedAttachments, setSelectedAttachments] = useState([]);
	const [splitSize, setSplitSize] = useState(SPLIT_SIZES.NO_ATTCH);
	const [isSearchPMVisible, setIsSearchPMVisible] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showAttachments, setShowAttachments] = useState(false);
	const [showAttchForm, setShowAttchForm] = useState(false);

	// Styles
	const text_theme = handleTextTheme({ themeId: currentOfficeTheme.themeId });

	useEffect(() => {
		// Handling Attachments on mail change in Attachment doc
		fnMailItemChangeCallBack();
	}, []);

	useEffect(() => {
		if (!activeNode) return;

		setRows(definedGridRows(activeNode, nodeData?.items || []));
	}, [activeNode, nodeData]);

	useEffect(() => {
		if (!activeNode) return;
		// Setting Split size and attachment doc visibility
		const visiblityCheck = handleAttachmentVisiblity(activeNode);
		setShowAttachments(visiblityCheck.isAttachmentDocVisible);
		setSplitSize(visiblityCheck.size);
		// Setting columns
		setColumns(definedGridColumns(activeNode, null, () => {}));
		setIsSearchPMVisible(activeNode === CMIC_TREE_NODE_TYPES.PROJECT_DOCUMENT_TYPE);
	}, [activeNode]);

	const fnMailItemChangeCallBack = useCallback(() => {
		// Adding Attachments to the doc
		let item = Office?.context?.mailbox?.item;
		let obj = JSON.parse(
			JSON.stringify({
				messageId: item?.itemId,
				attachments: [
					...item.attachments.map(data => {
						let newData = { ...data, originalName: data.name };
						return newData;
					}),
				],
				name: item.subject,
				from: item.from.displayName,
				dateTimeCreated: item.dateTimeCreated,
			}),
		);

		setItemFromEmail(obj);
	}, [Office]);

	const handleDocItemSearch = useDebounce(event => {
		if (!isInternetAvailable(setShowToast)) {
			return;
		}
		fetchDataPagination(activeNode, { num: 0, search: event?.target?.value || '' });
	}, 800);

	const handleAttachmentsSubmit = async (attachments = [], mailInfo) => {
		if (!isInternetAvailable(setShowToast)) {
			return;
		}
		setIsSubmitting(true);
		const result = await gatherAttchData(attachments);
		if (activeNode === CMIC_TREE_NODE_TYPES.PROJECT_DOCUMENT_TYPE_REVISION) {
			setSelectedAttachments(result);
			setShowAttchForm(true);
			setIsSubmitting(false);
		} else {
			// Handling atachments data
			// Checking if there's any required field
			const reqFields = await customFields(userInfo, {
				docType: 'PMDOCUMENT',
				docSubType: docDataForUpload[CMIC_TREE_NODE_TYPES.PROJECT_DOCUMENT_TYPE].PmdgtTypeCode,
			});

			if (reqFields.length > 0) {
				setRequiredFields(reqFields);
				setSelectedAttachments(result);
				setShowAttchForm(true);
				setIsSubmitting(false);
			} else {
				handleAttachmentsUpload(result);
			}
		}
	};

	// @desc determine if we want to upload doc as new document type or as a revision
	const handleFormSubmit = useCallback(
		fields => {
			setShowAttchForm(false);
			setIsSubmitting(true);

			if (!selectedAttachments || !selectedAttachments.length) return setIsSubmitting(false);

			if (activeNode === CMIC_TREE_NODE_TYPES.PROJECT_DOCUMENT_TYPE_REVISION) {
				handleRevisionsUpload(selectedAttachments, fields);
			} else {
				handleAttachmentsUpload(selectedAttachments, fields);
			}
		},
		[selectedAttachments, activeNode],
	);

	//@desc Uploading new revisions
	const handleRevisionsUpload = async (attachments, fields = { defaultFields: {}, customFields: [] }) => {
		// Uploading data to DB and returning array of attachments failed
		const result = await uploadRevisions(attachments, userInfo, {
			...fields.defaultFields,
			...docDataForUpload[activeNode],
		});

		handleParentUpdate(attachments, result.failedAttachments);
	};

	// @desc uploading new attachments
	const handleAttachmentsUpload = async (attachments, fields = { defaultFields: {}, customFields: [] }) => {
		// Uploading data to DB and returning array of attachments failed
		const result = await uploadAttachments(
			attachments,
			userInfo,
			selectedProject,
			{
				...fields.defaultFields,
				...docDataForUpload[activeNode],
			},
			fields.customFields,
		);

		handleParentUpdate(attachments, result.failedAttachments);
	};

	// @desc Updating count for parent node and refreshing data
	const handleParentUpdate = (attachments = [], failedAttachments = []) => {
		handleParentCountUpdate(attachments.length - failedAttachments.length, () =>
			fetchDataPagination(activeNode, { num: 0 }),
		);
		setIsSubmitting(false);
	};

	const handleExistedAttachmentContainerSize = size => {
		setSplitSize(size);
	};

	const handleFormVisibility = () => {
		setShowAttchForm(prev => !prev);
	};

	if (showAttchForm) {
		return (
			<UploadDocumentAttch
				docData={docDataForUpload}
				requiredFields={requiredFields}
				itemFromEmail={itemFromEmail}
				activeNode={activeNode}
				parentClassName={showAttchForm ? 'animate-fade-in' : 'animate-fade-out'}
				hideForm={handleFormVisibility}
				onSubmitForm={handleFormSubmit}
			/>
		);
	}

	let attachmentsUI = null;

	if (showAttachments) {
		attachmentsUI = (
			<ActionUploadAttachment
				btnLabel={isSubmitting ? 'SAVING FILES' : 'SAVE SELECTED FILES'}
				isDisabled={
					!docDataForUpload[CMIC_TREE_NODE_TYPES.PROJECT_DOCUMENT_TYPE]?.isDocUploadable || isSubmitting
				}
				isAttachmentVisible={splitSize === SPLIT_SIZES.MIN ? false : true}
				itemFromEmail={itemFromEmail}
				onBtnPress={handleAttachmentsSubmit}
				handleContainerSizeChange={handleExistedAttachmentContainerSize}
			/>
		);
	}

	return (
		<Stack
			className={showAttchForm ? 'animate-fade-out' : 'animate-fade-in'}
			styles={parentStackStyles}
			style={{ padding: '0.1rem' }}
			tokens={HeaderMargin}
		>
			<HeaderAttchDocuments />
			{isSearchPMVisible && (
				<SearchBox
					defaultValue={pageInfo[activeNode]?.search || ''}
					className={text_theme}
					placeholder={`Search ${breadcrumbs[0]?.label || ''}`}
					aria-label={`Search ${breadcrumbs[0]?.label}`}
					role="Search"
					autoComplete="off"
					disabled={isLoading}
					onChange={handleDocItemSearch}
					styles={searchInputStyles(currentOfficeTheme)}
				/>
			)}

			<CustomDatagrid
				rows={rows}
				columns={columns}
				currentOfficeTheme={currentOfficeTheme}
				disableColumnMenu={handleDisableColumnMenu(activeNode)}
			/>

			<Stack>{attachmentsUI}</Stack>
		</Stack>
	);
};

export default AttDocumesnts;
